import { randUuid } from "@/libraries/helper";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";
import CountryList from "country-list-with-dial-code-and-flag";
import type React from "react";
import { type ChangeEvent, useCallback, useEffect, useState } from "react";

interface PhoneNumberProps
	extends Omit<React.ComponentPropsWithoutRef<"input">, "value" | "onChange"> {
	value?: string;
	errorMessage?: string;
	phone?: Record<"dial_code" | "phone_number", string>;
	onChange: (value: {
		dial_code: string;
		country_name: string;
		phone_number: string;
		code: string;
	}) => void;
}

const PhoneNumber: React.FC<PhoneNumberProps> = ({
	value = "",
	phone: phoneProps,
	errorMessage,
	onChange,
	...inputProps
}) => {
	const countries = CountryList.getAll();
	// malaysia default country
	const [selectedCountry, setSelectedCountry] = useState(countries[136]);
	const [phoneNumber, setPhoneNumber] = useState(value);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [isDisabled, setIsDisabled] = useState(
		inputProps.readOnly || inputProps.disabled || false,
	);
	const [search, setSearch] = useState("");

	const findCountryByDialCode = useCallback(
		(phone: string) => {
			if (phone && phone.trim() !== "") {
				const foundCountry = countries.find((country) =>
					phone.startsWith(country.dial_code.replace("+", "")),
				);
				return foundCountry || null; // or handle case when no country is found
			}
			return null; // handle case when phone is empty or null
		},
		[countries],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		let phone = JSON.parse(localStorage.getItem("phone_number") || "{}");
		if (!phone?.phone_number && phoneProps?.phone_number) {
			phone = phoneProps;
		}
		if (phone?.phone_number) {
			setIsFocused(true);
			const dial_code = phone?.dial_code;
			const initialCountry = findCountryByDialCode(dial_code.replace("+", ""));
			if (initialCountry) {
				setSelectedCountry(initialCountry);
				setPhoneNumber(phone?.phone_number);
				if (
					inputProps.readOnly === undefined ||
					inputProps.disabled === undefined
				) {
					setIsDisabled(true);
				}
				onChange(phone);
			}
		}
	}, []);

	const handleInputFocus = useCallback(
		(e?: React.FocusEvent<HTMLInputElement>) => {
			setIsFocused(true);
			e && inputProps.onFocus?.(e);
		},
		[inputProps.onFocus],
	);

	const handleChangeCountry = useCallback(
		(country: (typeof countries)[0]) => {
			setSelectedCountry(country);
			setIsDropdownOpen(false);
			onChange({
				dial_code: country.dial_code,
				country_name: country.name,
				phone_number: country.dial_code,
				code: country.code,
			});
			handleInputFocus();
		},
		[onChange, handleInputFocus],
	);

	const handlePhoneNumberChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const inputPhoneNumber = event.target.value.trim();

			setPhoneNumber(inputPhoneNumber);

			onChange({
				dial_code: selectedCountry?.dial_code || "",
				country_name: selectedCountry?.name || "",
				phone_number: inputPhoneNumber,
				code: selectedCountry?.code || "",
			});
		},
		[onChange, selectedCountry],
	);

	const handleInputBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			if (phoneNumber.trim() === "") {
				setIsFocused(false);
			}
			inputProps.onBlur?.(e);
		},
		[phoneNumber, inputProps.onBlur],
	);

	const filteredCountries = countries.filter(
		(country) =>
			country.name.toLowerCase().includes(search.toLowerCase()) ||
			country.dial_code.includes(search),
	);

	return (
		<div>
			<div
				className={`phone-number-input ${errorMessage ? "error" : ""}`}
				style={{ position: "relative" }}
			>
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
				<div
					className="custom-select"
					onClick={
						isDisabled ? undefined : () => setIsDropdownOpen((prev) => !prev)
					}
				>
					<div className="w-5 h-5 rounded-full flex flex-col justify-center items-center border border-solid overflow-hidden">
						<img
							src={`/images/countries-icon/${selectedCountry.code}.svg`}
							alt={selectedCountry.name}
						/>
					</div>
					<span>{selectedCountry.dial_code}</span>
					{isDropdownOpen ? (
						<ArrowDropUpRoundedIcon />
					) : (
						<ArrowDropDownRoundedIcon />
					)}
				</div>
				{isDropdownOpen && (
					<div className="custom-options">
						<div className="relative w-full h-52 overflow-hidden">
							<input
								aria-label="search"
								className="absolute w-full h-12 top-0 inset-x-0 border-2 outline-none rounded-lg px-2"
								type="search"
								value={search}
								onChange={(e) => setSearch(e.target.value)}
							/>
							<div className="w-full h-full overflow-y-auto scroll-hidden mt-16">
								{filteredCountries.map((country) => (
									// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
									<div
										key={randUuid()}
										onClick={() => handleChangeCountry(country)}
										style={{
											display: "flex",
											alignItems: "center",
											padding: "5px",
											cursor: "pointer",
										}}
									>
										<img
											src={`/images/countries-icon/${country.code}.svg`}
											alt={country.name}
											style={{ marginRight: "8px" }}
										/>
										{country.name} ({country.dial_code})
									</div>
								))}
							</div>
						</div>
					</div>
				)}
				<div className="phone-input">
					<label
						htmlFor="phone"
						className={`absolute transition-all bg-white ${
							isFocused ? "-top-3 -left-28 scale-75" : "top-1/2 left-0"
						} text-gray-100 -translate-y-2/4 text-base z-10 px-1`}
					>
						Phone Number
					</label>
					<input
						id="phone"
						type="number"
						value={phoneNumber}
						onChange={handlePhoneNumberChange}
						onBlur={handleInputBlur}
						onFocus={handleInputFocus}
						readOnly={isDisabled}
						{...inputProps}
					/>
				</div>
			</div>
			{errorMessage && <span className="error-message">{errorMessage}</span>}
		</div>
	);
};

export default PhoneNumber;
